import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { ASSETS } from '@/lib/assets';

/**
 * Apple-style scroll-pinned product detail section.
 * Each "chapter" has its own image, stat, body and 3 supporting feature pills,
 * plus a floating "blueprint" detail card so the right side never feels sparse.
 */
const steps = [
  {
    label: 'Double-Wall Vacuum Insulation',
    body: 'Iced coffee at 9am stays iced at 5pm. A thermal shell precision-engineered between two walls of 304 surgical-grade steel.',
    stat: '24h',
    statLabel: 'Cold Retention',
    image: ASSETS.heroNavySkyline,
    detail: ASSETS.heroBlackMarble,
    detailLabel: 'Cross-Section',
    detailNote: '304 STEEL · VACUUM SEAL',
    pills: [
      { k: 'Cold', v: '24 hours' },
      { k: 'Hot', v: '12 hours' },
      { k: 'Walls', v: 'Vacuum-sealed steel' },
    ],
  },
  {
    label: 'Leakproof, Always.',
    body: 'A re-engineered slide lid with triple-seal silicone. Sling it in a tote, drop it in a backpack — KIVO never spills.',
    stat: '0',
    statLabel: 'Drips Out',
    image: ASSETS.topAngleBlack,
    detail: ASSETS.backAngleBlack,
    detailLabel: 'Lid Mechanism',
    detailNote: 'TRIPLE-SEAL SILICONE',
    pills: [
      { k: 'Seal', v: 'Triple silicone ring' },
      { k: 'Action', v: 'Slide-to-lock' },
      { k: 'Drop-test', v: '1.2m, no spill' },
    ],
  },
  {
    label: 'Fits Every Workday.',
    body: 'Designed around the millimetres that matter — cup holders, laptop sleeves, gym side-pockets. Wherever you go, KIVO comes along.',
    stat: '700ml',
    statLabel: '24oz Capacity',
    image: ASSETS.heroStoneGreyDesk,
    detail: ASSETS.genzOfficeIndia,
    detailLabel: 'Daily Carry',
    detailNote: 'TOTE · LAPTOP BAG · CAR',
    pills: [
      { k: 'Base', v: 'Ø 72 mm' },
      { k: 'Height', v: '215 mm' },
      { k: 'Fits', v: 'Standard cup holders' },
    ],
  },
];

export default function ProductDetail() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

  const rotate = useTransform(scrollYProgress, [0, 1], [-3, 6]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.05, 1]);

  // Per-step crossfade opacities (hooks declared at top level — react rules)
  const opacity0 = useTransform(scrollYProgress, [-0.05, 0.05, 0.28, 0.38], [0, 1, 1, 0]);
  const opacity1 = useTransform(scrollYProgress, [0.28, 0.38, 0.62, 0.72], [0, 1, 1, 0]);
  const opacity2 = useTransform(scrollYProgress, [0.62, 0.72, 1, 1.1], [0, 1, 1, 1]);
  const opacities = [opacity0, opacity1, opacity2];

  const labelA = useTransform(scrollYProgress, [0, 0.15, 0.35], [0, 1, 0]);
  const labelB = useTransform(scrollYProgress, [0.3, 0.5, 0.7], [0, 1, 0]);
  const labelC = useTransform(scrollYProgress, [0.6, 0.8, 1], [0, 1, 1]);

  // Step-progress indicator (0, 1, 2 ↔ scroll)
  const stepIndex = useTransform(scrollYProgress, [0, 0.34, 0.67, 1], [0, 1, 2, 2]);

  return (
    <section
      id="product"
      ref={ref}
      className="relative bg-[#F3EFE8] overflow-hidden"
      style={{ height: '300vh' }}
    >
      <div className="sticky top-0 h-[100svh] w-full">
        {/* Vertical step rail */}
        <div className="absolute left-6 lg:left-12 top-1/2 -translate-y-1/2 z-20 hidden md:flex flex-col gap-3 text-[10px] tracking-[0.3em] uppercase text-[#111]/35">
          {steps.map((s, i) => (
            <motion.div
              key={s.label}
              style={{ opacity: opacities[i] }}
              className="flex items-center gap-3"
            >
              <span className="w-6 h-px bg-[#111]" />
              <span className="text-[#111]">0{i + 1} / 03</span>
            </motion.div>
          ))}
        </div>

        <div className="max-w-[1400px] mx-auto h-full px-6 lg:px-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Sticky bottle stage — crossfade between premium renders */}
          <div className="lg:col-span-6 relative h-[78vh] flex items-center justify-center">
            <motion.div
              style={{ rotate, scale }}
              className="relative h-full w-full max-w-[560px] rounded-3xl overflow-hidden"
            >
              {steps.map((s, i) => (
                <motion.img
                  key={s.image}
                  src={s.image}
                  alt={s.label}
                  loading="lazy"
                  style={{ opacity: opacities[i] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ))}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[60%] h-6 rounded-[50%] bg-black/15 blur-2xl" />
            </motion.div>

            {/* Blueprint detail card (changes per step) */}
            <div className="absolute -right-2 sm:right-4 bottom-6 hidden sm:block z-10">
              {steps.map((s, i) => (
                <motion.div
                  key={`detail-${s.label}`}
                  style={{ opacity: opacities[i] }}
                  className="absolute right-0 bottom-0 w-44 lg:w-52"
                >
                  <div className="bg-[#FAF8F4]/95 backdrop-blur-md border border-[#111]/8 rounded-2xl overflow-hidden shadow-xl shadow-black/10">
                    <div
                      className="aspect-[4/3] bg-cover bg-center"
                      style={{ backgroundImage: `url(${s.detail})` }}
                    />
                    <div className="px-4 py-3">
                      <div className="text-[9px] tracking-[0.3em] uppercase text-[#CBBBA0] mb-1">
                        {s.detailNote}
                      </div>
                      <div className="font-display text-sm text-[#111]">{s.detailLabel}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Floating measurement labels */}
            <motion.div
              style={{ opacity: labelA }}
              className="absolute top-[8%] right-2 sm:right-4 text-[11px] tracking-[0.25em] uppercase text-[#111]/70 flex items-center gap-3 bg-[#FAF8F4]/90 backdrop-blur px-3 py-2 rounded-full"
            >
              <span className="h-px w-10 bg-[#111]/30" />
              304 Stainless Steel
            </motion.div>
            <motion.div
              style={{ opacity: labelB }}
              className="absolute top-[8%] left-2 sm:left-4 text-[11px] tracking-[0.25em] uppercase text-[#111]/70 flex items-center gap-3 bg-[#FAF8F4]/90 backdrop-blur px-3 py-2 rounded-full"
            >
              Triple-Seal Lid
              <span className="h-px w-10 bg-[#111]/30" />
            </motion.div>
            <motion.div
              style={{ opacity: labelC }}
              className="absolute bottom-[40%] right-2 sm:right-4 text-[11px] tracking-[0.25em] uppercase text-[#111]/70 flex items-center gap-3 bg-[#FAF8F4]/90 backdrop-blur px-3 py-2 rounded-full"
            >
              <span className="h-px w-10 bg-[#111]/30" />
              700ml · 24oz
            </motion.div>
          </div>

          {/* Scrolling text panels */}
          <div className="lg:col-span-6 relative h-[78vh] flex items-center">
            <div className="relative w-full">
              {steps.map((s, i) => (
                <motion.div
                  key={s.label}
                  style={{ opacity: opacities[i] }}
                  className="absolute inset-0 flex flex-col justify-center"
                >
                  <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0]">
                    <span>0{i + 1} / 03</span>
                    <span className="h-px w-10 bg-[#CBBBA0]/50" />
                    <span className="text-[#111]/40">Engineering</span>
                  </div>

                  <h3 className="mt-4 font-display text-[clamp(2rem,4.5vw,3.5rem)] leading-[1] tracking-[-0.02em] text-[#111]">
                    {s.label}
                  </h3>

                  <p className="mt-6 max-w-md text-[16px] leading-relaxed text-[#111]/65">
                    {s.body}
                  </p>

                  {/* Feature pills (key/value lockup) */}
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md">
                    {s.pills.map((p) => (
                      <div
                        key={p.k}
                        className="bg-[#FAF8F4]/70 border border-[#111]/10 rounded-xl px-4 py-3"
                      >
                        <div className="text-[9px] tracking-[0.3em] uppercase text-[#111]/45">
                          {p.k}
                        </div>
                        <div className="text-[13px] font-medium text-[#111] mt-1 leading-tight">
                          {p.v}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-10 flex items-end gap-12">
                    <div>
                      <div className="font-display text-5xl leading-none text-[#111]">
                        {s.stat}
                      </div>
                      <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[#111]/50">
                        {s.statLabel}
                      </div>
                    </div>
                    <div className="hidden md:flex flex-col gap-1 text-[11px] tracking-[0.25em] uppercase text-[#111]/40">
                      <span>BPA Free</span>
                      <span>Lab Tested</span>
                      <span>Lifetime Care</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer band */}
        <div className="absolute bottom-8 left-0 right-0 px-6 lg:px-12">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[#111]/40">
            <span>Built for Everyday Carry</span>
            <span>Tote · Laptop Bag · Desk</span>
          </div>
        </div>
      </div>
    </section>
  );
}
